import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  PhoneCall,
  Plus,
  Check,
  X,
  Clock,
  Voicemail,
  PhoneMissed,
  PhoneOutgoing,
  AlarmClock,
  User,
  Briefcase,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// One-screen triage queue for callback requests (receptionist messages +
// manual callback promises). Sorted by due_by; color = urgency/overdue.
// Deliberately NOT a CRM: look, act (attempt/resolve/dismiss/snooze), move on.

const URGENCY_STYLES = {
  critical: 'bg-red-500/20 text-red-300 border-red-500/40',
  elevated: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  normal: 'bg-virgo-teal-800/40 text-virgo-teal-200 border-virgo-teal-700',
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const dueLabel = (item) => {
  if (!item.due_by) return '';
  const ms = new Date(item.due_by) - Date.now();
  const abs = Math.abs(ms);
  const hrs = Math.floor(abs / 3600000);
  const mins = Math.floor((abs % 3600000) / 60000);
  const span = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  return ms < 0 ? `overdue by ${span}` : `due in ${span}`;
};

const Callbacks = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('active');
  const [includeSnoozed, setIncludeSnoozed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/callbacks?status_filter=${filter}&include_snoozed=${includeSnoozed}`
      );
      setItems(r.data.items || []);
    } catch (e) {
      console.error('callbacks load failed', e);
    } finally {
      setLoading(false);
    }
  }, [filter, includeSnoozed]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/callbacks/${id}/${action}`, body);
      setNoteDraft('');
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  const toggleExpand = (item) => {
    const next = expanded === item.id ? null : item.id;
    setExpanded(next);
    setNoteDraft('');
    if (next && !item.first_viewed_at) {
      api.post(`/callbacks/${item.id}/viewed`).catch(() => {});
    }
  };

  const onDismiss = (id) => {
    const reason = window.prompt('Dismiss reason (required):');
    if (reason && reason.trim()) act(id, 'dismiss', { reason: reason.trim() });
  };

  const onSnooze = (id) => {
    const raw = window.prompt('Snooze until (YYYY-MM-DD or YYYY-MM-DD HH:MM local):');
    if (!raw) return;
    const d = new Date(raw.includes(':') ? raw : `${raw} 09:00`);
    if (isNaN(d.getTime())) { alert('Could not parse that date'); return; }
    act(id, 'snooze', { until: d.toISOString() });
  };

  const overdueCount = items.filter((i) => i.overdue).length;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <PhoneCall className="text-virgo-gold-400" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-white">Callbacks</h1>
            <p className="text-sm text-virgo-teal-300">
              {items.length} in view{overdueCount > 0 && (
                <span className="text-red-400"> · {overdueCount} overdue</span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center space-x-2 bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white px-4 py-2 rounded-lg"
        >
          <Plus size={18} /><span>Log callback</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2 mb-4">
        {['active', 'resolved', 'dismissed', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
              filter === f
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-300 hover:bg-virgo-navy-600'
            }`}
          >
            {f}
          </button>
        ))}
        <label className="flex items-center space-x-2 text-sm text-virgo-teal-300 ml-4 cursor-pointer">
          <input
            type="checkbox"
            checked={includeSnoozed}
            onChange={(e) => setIncludeSnoozed(e.target.checked)}
          />
          <span>show snoozed</span>
        </label>
      </div>

      {/* Queue */}
      {loading ? (
        <div className="text-virgo-teal-300 py-12 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-virgo-teal-400 py-16 text-center border border-dashed border-virgo-navy-600 rounded-xl">
          Queue is clear. <span className="text-virgo-teal-200">Nothing waiting on a callback.</span>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const isOpen = expanded === item.id;
            return (
              <div
                key={item.id}
                className={`rounded-xl border ${
                  item.overdue
                    ? 'border-red-500/60 bg-red-950/20'
                    : 'border-virgo-navy-600 bg-virgo-navy-800/60'
                }`}
              >
                {/* Row */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer"
                  onClick={() => toggleExpand(item)}
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${URGENCY_STYLES[item.urgency] || URGENCY_STYLES.normal}`}>
                      {item.urgency}
                    </span>
                    <span className="text-white font-medium truncate">
                      {item.caller_name || 'Unknown caller'}
                    </span>
                    <span className="text-virgo-teal-400 text-sm">{item.caller_phone}</span>
                    {item.matched_contact_id && (
                      <User size={14} className="text-virgo-gold-400" title="Known contact" />
                    )}
                    {item.matched_case_id && (
                      <Briefcase size={14} className="text-virgo-gold-400" title="Open case linked" />
                    )}
                    {item.snoozed && (
                      <span className="text-xs text-virgo-teal-400 flex items-center space-x-1">
                        <AlarmClock size={12} /><span>snoozed</span>
                      </span>
                    )}
                    {item.status === 'attempted' && (
                      <span className="text-xs text-virgo-teal-400">attempted ×{(item.attempts || []).filter(a => ['reached','voicemail','no-answer'].includes(a.outcome)).length}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 flex-shrink-0">
                    <span className={`text-sm ${item.overdue ? 'text-red-400 font-semibold' : 'text-virgo-teal-300'}`}>
                      <Clock size={14} className="inline mr-1" />
                      {dueLabel(item)}
                    </span>
                    {isOpen ? <ChevronUp size={18} className="text-virgo-teal-400" /> : <ChevronDown size={18} className="text-virgo-teal-400" />}
                  </div>
                </div>

                {/* Detail */}
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-virgo-navy-600 pt-3">
                    <p className="text-virgo-teal-100 whitespace-pre-wrap mb-3">
                      {item.message_text || '(no message text)'}
                    </p>
                    <div className="text-xs text-virgo-teal-400 mb-3">
                      {item.source} · received {fmtWhen(item.created_at)} · due {fmtWhen(item.due_by)}
                      {item.snoozed_until && <> · snoozed until {fmtWhen(item.snoozed_until)}</>}
                    </div>

                    {/* Attempts / audit trail */}
                    {(item.attempts || []).length > 0 && (
                      <div className="mb-3 space-y-1">
                        {item.attempts.map((a, i) => (
                          <div key={i} className="text-xs text-virgo-teal-300">
                            <span className="text-virgo-teal-500">{fmtWhen(a.at)}</span>{' '}
                            <span className="text-virgo-gold-400">{a.by}</span>{' '}
                            <span className="font-medium">{a.outcome}</span>
                            {a.note ? <span> — {a.note}</span> : null}
                          </div>
                        ))}
                      </div>
                    )}

                    {['open', 'attempted'].includes(item.status) && (
                      <>
                        <input
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          placeholder="Optional note for the next action…"
                          className="w-full mb-2 px-3 py-2 rounded-lg bg-virgo-navy-900 border border-virgo-navy-600 text-white text-sm"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button disabled={busy} onClick={() => act(item.id, 'attempt', { outcome: 'reached', note: noteDraft })}
                            className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-virgo-teal-700 hover:bg-virgo-teal-600 text-white text-sm">
                            <PhoneOutgoing size={14} /><span>Reached</span>
                          </button>
                          <button disabled={busy} onClick={() => act(item.id, 'attempt', { outcome: 'voicemail', note: noteDraft })}
                            className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-virgo-navy-600 hover:bg-virgo-navy-500 text-white text-sm">
                            <Voicemail size={14} /><span>Voicemail</span>
                          </button>
                          <button disabled={busy} onClick={() => act(item.id, 'attempt', { outcome: 'no-answer', note: noteDraft })}
                            className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-virgo-navy-600 hover:bg-virgo-navy-500 text-white text-sm">
                            <PhoneMissed size={14} /><span>No answer</span>
                          </button>
                          <span className="flex-1" />
                          <button disabled={busy} onClick={() => onSnooze(item.id)}
                            className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-virgo-navy-600 hover:bg-virgo-navy-500 text-virgo-teal-200 text-sm">
                            <AlarmClock size={14} /><span>Snooze</span>
                          </button>
                          <button disabled={busy} onClick={() => onDismiss(item.id)}
                            className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-virgo-navy-600 hover:bg-red-900/60 text-virgo-teal-200 text-sm">
                            <X size={14} /><span>Dismiss</span>
                          </button>
                          <button disabled={busy} onClick={() => act(item.id, 'resolve', { note: noteDraft })}
                            className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm">
                            <Check size={14} /><span>Resolve</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
};

const CreateModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    caller_name: '', caller_phone: '', message_text: '', urgency: '', note: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.urgency) delete body.urgency; // let the rules classify
      await api.post('/callbacks/manual', body);
      onCreated();
    } catch (err) {
      alert(err?.response?.data?.detail || 'create failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl p-6 w-full max-w-md space-y-3"
      >
        <h2 className="text-lg font-semibold text-white mb-1">Log a callback</h2>
        <p className="text-xs text-virgo-teal-400 mb-2">
          A promise to call someone back, from any source. Leave urgency blank to auto-classify.
        </p>
        <input required placeholder="Caller name" value={form.caller_name} onChange={set('caller_name')}
          className="w-full px-3 py-2 rounded-lg bg-virgo-navy-900 border border-virgo-navy-600 text-white text-sm" />
        <input placeholder="Phone (optional)" value={form.caller_phone} onChange={set('caller_phone')}
          className="w-full px-3 py-2 rounded-lg bg-virgo-navy-900 border border-virgo-navy-600 text-white text-sm" />
        <textarea placeholder="What's it about / what was promised" rows={3}
          value={form.message_text} onChange={set('message_text')}
          className="w-full px-3 py-2 rounded-lg bg-virgo-navy-900 border border-virgo-navy-600 text-white text-sm" />
        <select value={form.urgency} onChange={set('urgency')}
          className="w-full px-3 py-2 rounded-lg bg-virgo-navy-900 border border-virgo-navy-600 text-white text-sm">
          <option value="">Urgency: auto-classify</option>
          <option value="normal">normal — next business day</option>
          <option value="elevated">elevated — 4 business hours</option>
          <option value="critical">critical — 2 hours</option>
        </select>
        <input placeholder="Note (e.g. 'call proposed for Friday afternoon')" value={form.note} onChange={set('note')}
          className="w-full px-3 py-2 rounded-lg bg-virgo-navy-900 border border-virgo-navy-600 text-white text-sm" />
        <div className="flex justify-end space-x-2 pt-1">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg bg-virgo-navy-600 text-virgo-teal-200 text-sm">Cancel</button>
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white text-sm">
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Callbacks;
